import {
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

/**
 * Persisted rail preferences (per browser, localStorage-backed).
 *
 *  - pinned    : keep the rail expanded with text labels (UX review option C).
 *  - coachmark : one-time hint pointing users at the hidden labels (option D).
 *
 * Both reads are SSR-safe and swallow storage errors (private mode, quota).
 */

const PINNED_KEY = "alphacore.rail.pinned";
const COACH_KEY = "alphacore.rail.coachSeen";

function readBool(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : v === "1";
  } catch {
    return fallback;
  }
}

function writeBool(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, value ? "1" : "0");
  } catch {
    // ignore (private mode / quota)
  }
}

/**
 * Returns the raw React dispatcher so callers can use a functional update
 * (`setPinned(p => !p)`) — this is load-bearing for the pin toggle, which is
 * clicked rapidly: reading the previous value from a render closure would let
 * back-to-back clicks fight each other. Persistence runs in an effect so it
 * stays a pure setter.
 */
export function useRailPinned(): [boolean, Dispatch<SetStateAction<boolean>>] {
  const [pinned, setPinned] = useState<boolean>(() =>
    readBool(PINNED_KEY, false),
  );
  useEffect(() => {
    writeBool(PINNED_KEY, pinned);
  }, [pinned]);
  return [pinned, setPinned];
}

export function useRailCoachmark(): { seen: boolean; markSeen: () => void } {
  const [seen, setSeen] = useState<boolean>(() => readBool(COACH_KEY, false));
  const markSeen = useCallback(() => {
    setSeen(true);
    writeBool(COACH_KEY, true);
  }, []);
  return { seen, markSeen };
}
